import { Leaf, ShieldCheck, Sprout, BookOpen, TrendingUp, Factory, Eye, Target } from "lucide-react";
import { Seo } from "@/components/Seo";

const ABOUT_IMG = "https://customer-assets-v7afamib.emergentagent.net/job_yg-organic-rebuild/artifacts/97owky2e_Cocoa-33.jpg";
const PLANT_IMG = "https://customer-assets-v7afamib.emergentagent.net/job_yg-organic-rebuild/artifacts/gkkol0el_PHYTOSOL%203.jpg";

export default function About() {
  return (
    <div>
      <Seo
        title="About Us"
        path="/about"
        description="Yayra Glover Organic Inputs strengthens Ghana's organic commodity value chain — carrying forward Yayra Glover's certified-organic cocoa legacy with locally-formulated organic fertilisers and crop protection."
      />
      {/* Hero */}
      <section className="bg-brand text-brand-bg noise-overlay">
        <div className="mx-auto max-w-4xl px-6 py-24 text-center">
          <span className="text-xs uppercase tracking-[0.28em] text-brand-ochre font-semibold">About Us</span>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            Strengthening the organic value chain & empowering Ghanaian farmers.
          </h1>
          <p className="mt-6 text-brand-bg/80 text-lg max-w-2xl mx-auto leading-relaxed">
            We help farmers grow healthier crops and richer harvests — without the harmful chemicals
            that damage soil, water and communities.
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="mx-auto max-w-6xl px-6 py-20 grid gap-12 md:grid-cols-2 items-center">
        <img src={ABOUT_IMG} alt="Farmers harvesting certified-organic cocoa in Ghana" className="rounded-sm w-full h-[440px] object-cover" />
        <div>
          <span className="text-xs uppercase tracking-[0.2em] text-brand-muted font-semibold">The team behind the vision</span>
          <h2 className="mt-2 font-display text-3xl sm:text-4xl text-brand leading-tight">Rooted in a proven organic movement</h2>
          <p className="mt-5 text-brand-muted leading-relaxed">
            Yayra Glover Organic Inputs carries forward the vision of Yayra Glover — widely known as the father of
            certified organic cocoa in Ghana. For years, thousands of smallholder farmers in the Eastern Region
            have grown premium, certified-organic cocoa using natural methods.
          </p>
          <p className="mt-4 text-brand-muted leading-relaxed">
            Today we bring that same philosophy to every farm across Ghana with a range of organic foliar
            fertilisers, biostimulants and crop-protection products — formulated to boost yield while keeping
            your land alive and productive for the next generation.
          </p>
        </div>
      </section>

      {/* Resources & Impact */}
      <section className="bg-brand-surface">
        <div className="mx-auto max-w-6xl px-6 py-20">
          <h2 className="font-display text-3xl sm:text-4xl text-brand text-center">Knowledge, adoption & impact</h2>
          <p className="mt-3 text-brand-muted text-center max-w-2xl mx-auto">
            Beyond products, we invest in the farmers and communities behind Ghana's organic commodity value chain.
          </p>

          <div className="mt-12 grid gap-6 sm:grid-cols-2" data-testid="farmer-education-photos">
            <figure className="rounded-sm overflow-hidden bg-white border border-brand/10">
              <img
                src="https://customer-assets-v7afamib.emergentagent.net/job_yg-organic-rebuild/artifacts/q61056w1_IMG-20231122-WA0007.jpg"
                alt="Yayra Glover field officer leading a farmer training session under a mango tree in rural Ghana"
                className="w-full h-64 object-cover"
              />
              <figcaption className="px-5 py-4 text-sm text-brand-muted">
                Hands-on farmer training sessions bring organic best-practices directly to the community.
              </figcaption>
            </figure>
            <figure className="rounded-sm overflow-hidden bg-white border border-brand/10">
              <img
                src="https://customer-assets-v7afamib.emergentagent.net/job_yg-organic-rebuild/artifacts/ky8ulp3e_IMG-20231122-WA0009.jpg"
                alt="An agronomist teaching a group of Ghanaian cocoa farmers about organic crop protection"
                className="w-full h-64 object-cover"
              />
              <figcaption className="px-5 py-4 text-sm text-brand-muted">
                Farmers learn how to apply organic inputs safely and get the most from every harvest.
              </figcaption>
            </figure>
          </div>

          <div className="mt-12 grid gap-6 sm:grid-cols-2">
            <div className="bg-white rounded-sm border border-brand/10 p-8">
              <BookOpen className="w-9 h-9 text-brand-terracotta" strokeWidth={1.5} />
              <h3 className="mt-4 font-display text-2xl text-brand">Resources & Education</h3>
              <p className="mt-2 text-sm text-brand-muted leading-relaxed">
                We empower your farming journey with knowledge — practical guides on organic practices,
                optimising yields, and effective pest management and disease prevention, plus the
                benefits of organic farming for your crops and the environment.
              </p>
            </div>
            <div className="bg-white rounded-sm border border-brand/10 p-8">
              <TrendingUp className="w-9 h-9 text-brand-terracotta" strokeWidth={1.5} />
              <h3 className="mt-4 font-display text-2xl text-brand">Farmer Adoption & Impact</h3>
              <p className="mt-2 text-sm text-brand-muted leading-relaxed">
                We track the adoption of organic insecticides among cocoa farmers and measure real
                improvements in pest-control efficacy, crop health and yield — with periodic assessments
                of the economic and environmental impact on organic cocoa farming.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Flagship project */}
      <section className="mx-auto max-w-6xl px-6 py-20 grid gap-12 md:grid-cols-2 items-center">
        <div className="order-2 md:order-1">
          <span className="text-xs uppercase tracking-[0.2em] text-brand-muted font-semibold">Flagship impact-driven project</span>
          <h2 className="mt-2 font-display text-3xl sm:text-4xl text-brand leading-tight flex items-center gap-3">
            <Factory className="w-8 h-8 text-brand-terracotta shrink-0" strokeWidth={1.5} />
            Local formulation in Suhum
          </h2>
          <p className="mt-5 text-brand-muted leading-relaxed">
            YGOIL intends to establish a local formulation plant in Suhum, Ghana, to process and formulate
            AgroPy 5EW concentrates sourced from Rwanda into finished products.
          </p>
          <p className="mt-4 text-brand-muted leading-relaxed">
            Producing locally addresses logistical challenges, reduces costs, and enhances market accessibility —
            putting affordable, certified-organic crop protection within reach of more Ghanaian farmers.
          </p>
        </div>
        <img src={PLANT_IMG} alt="Organic input formulation" className="order-1 md:order-2 rounded-sm w-full h-[400px] object-cover" />
      </section>

      {/* Values */}
      <section className="bg-brand-surface">
        <div className="mx-auto max-w-6xl px-6 py-20">
          <h2 className="font-display text-3xl sm:text-4xl text-brand text-center">What we stand for</h2>
          <div className="mt-12 grid gap-6 sm:grid-cols-3">
            {[
              { icon: Leaf, t: "Eco Friendly", d: "Products designed to protect soil health, water and biodiversity." },
              { icon: ShieldCheck, t: "Certified Organic", d: "Reliable, certified inputs trusted by commercial and smallholder farmers." },
              { icon: Sprout, t: "Non Toxic", d: "Safe for your crops, your family, and the people who eat your produce." },
            ].map((v) => (
              <div key={v.t} className="bg-white rounded-sm border border-brand/10 p-7">
                <v.icon className="w-9 h-9 text-brand-terracotta" strokeWidth={1.5} />
                <h3 className="mt-4 font-display text-xl text-brand">{v.t}</h3>
                <p className="mt-1.5 text-sm text-brand-muted leading-relaxed">{v.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="bg-brand text-brand-bg noise-overlay">
        <div className="mx-auto max-w-6xl px-6 py-20">
          <div className="grid gap-6 md:grid-cols-2">
            <div className="bg-white/5 border border-brand-bg/15 rounded-sm p-8">
              <div className="flex items-center gap-3">
                <Eye className="w-8 h-8 text-brand-ochre shrink-0" strokeWidth={1.5} />
                <span className="text-xs uppercase tracking-[0.2em] text-brand-ochre font-semibold">Our Vision</span>
              </div>
              <p className="mt-5 font-display text-2xl leading-snug">
                To strengthen the organic commodity value chain, with a focus on Ghana and Africa,
                in support of the UN Sustainable Development Goals 1, 2 and 3.
              </p>
            </div>
            <div className="bg-white/5 border border-brand-bg/15 rounded-sm p-8">
              <div className="flex items-center gap-3">
                <Target className="w-8 h-8 text-brand-ochre shrink-0" strokeWidth={1.5} />
                <span className="text-xs uppercase tracking-[0.2em] text-brand-ochre font-semibold">Our Mission</span>
              </div>
              <p className="mt-5 font-display text-2xl leading-snug">
                To be recognised as the leading organic-inputs distributor in sub-Saharan Africa —
                solving unsustainable agricultural practices and their harm to the environment,
                public health and long-term food security.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core values */}
      <section className="mx-auto max-w-6xl px-6 py-20 text-center">
        <span className="text-xs uppercase tracking-[0.2em] text-brand-muted font-semibold">Our Core Values</span>
        <h2 className="mt-2 font-display text-3xl sm:text-4xl text-brand">Green solutions for greener harvests</h2>
        <div className="mt-10 flex flex-wrap justify-center gap-3">
          {["Ethics", "Transparency", "Responsibility", "Professionalism", "Teamwork"].map((v) => (
            <span key={v} className="px-5 py-2.5 rounded-full bg-brand-surface border border-brand/10 text-brand font-medium text-sm">
              {v}
            </span>
          ))}
        </div>
      </section>
    </div>
  );
}
